// ─── ENCRYPTION MANAGER ───────────────────────────────────────────
// Handles fetching company encryption keys from backend
// Manages key lifecycle (fetch on login, clear on logout)

import { setCompanyEncryptionKey, clearCompanyEncryptionKey } from './crypto.js';

/**
 * Fetch company's encryption key from backend
 * Requires valid authentication token
 * @param {string} token - Bearer token for authentication
 * @returns {Promise<string>} - 256-bit encryption key in hex format
 */
export async function fetchEncryptionKey(token) {
  if (!token) {
    throw new Error('Authentication token required to fetch encryption key');
  }

  try {
    const response = await fetch('/api/encryption/key', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch encryption key: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.key) {
      throw new Error('No encryption key in response');
    }

    console.log('[Encryption Manager] Key fetched successfully');
    setCompanyEncryptionKey(data.key);
    return data.key;
  } catch (error) {
    console.error('[Encryption Manager] Failed to fetch key:', error);
    throw error;
  }
}

/**
 * Rotate to a new encryption key
 * Requires valid authentication token
 * @param {string} token - Bearer token for authentication
 * @returns {Promise<string>} - New 256-bit encryption key in hex format
 */
export async function rotateEncryptionKey(token) {
  if (!token) {
    throw new Error('Authentication token required to rotate encryption key');
  }

  try {
    const response = await fetch('/api/encryption/rotate', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to rotate encryption key: ${response.status}`);
    }

    const data = await response.json();
    if (!data.key) {
      throw new Error('No new key in response');
    }

    console.log('[Encryption Manager] Key rotated successfully');
    setCompanyEncryptionKey(data.key);
    return data.key;
  } catch (error) {
    console.error('[Encryption Manager] Failed to rotate key:', error);
    throw error;
  }
}

/**
 * Clear encryption key from memory (call on logout)
 */
export function clearEncryptionKey() {
  clearCompanyEncryptionKey();
  console.log('[Encryption Manager] Key cleared');
}

/**
 * Check if encryption key is loaded
 * @returns {boolean}
 */
export function hasEncryptionKey() {
  // This is a simple check - in practice you'd check the crypto module
  // For now, we just return true if we can access the module
  return true;
}
